<template>
  <div class="content">
    <base-header class="pb-6">
      <div class="row align-items-center py-4">
        <div class="col-lg-6 col-7">
          <h6 class="h2 text-white d-inline-block mb-0">Default Contract</h6>
          <nav aria-label="breadcrumb" class="d-none d-md-inline-block ml-md-4">
            <route-bread-crumb></route-bread-crumb>
          </nav>
        </div>
      </div>
    </base-header>

    <div class="container-fluid mt--6" v-loading="loading">
      <div class="row">
        <div class="col">
          <card>
            <div slot="header" class="row align-items-center">
              <div class="col-8">
                <h3 class="mb-0">
                  Contract Template Management
                </h3>
              </div>
              <div class="col-4 text-right">
                <a href="javascript:" class="btn btn-sm btn-secondary" @click="resetForm" :disabled="processing">
                  <i class="fa fa-refresh"></i> Reset
                </a>
                <a href="javascript:" class="btn btn-sm btn-primary" @click="saveContract" :disabled="!canSave">
                  <i class="fa fa-save"></i> Save Contract
                </a>
              </div>
            </div>
            <div>
              <h6 class="heading-small text-muted mb-4">Manage the default contract template used for generating quotes</h6>
            </div>
            <form id="contractForm" v-loading="saving">
              <div>
                <h6 class="heading-small text-muted mb-2">Contract Details</h6>
                <div class="row">
                  <div class="col-lg-6">
                    <base-input
                      type="text"
                      label="Contract Name *"
                      placeholder="Enter contract template name"
                      v-model="contractForm.name"
                      :disabled="processing"
                      required
                    >
                    </base-input>
                  </div>
                </div>
              </div>

              <div>
                <h6 class="heading-small text-muted mb-2">Upload Contract File *</h6>
                    <div class="file-upload-section">
                        <!-- Show upload area if no file is selected and no existing file -->
                        <div v-if="!contractForm.file && !contractForm.fileUrl" class="upload-area">
                            <el-upload
                            class="contract-uploader"
                            drag
                            action="#"
                            :before-upload="handleFileUpload"
                            :show-file-list="false"
                            accept=".pdf,.doc,.docx"
                            >
                            <i class="el-icon-upload"></i>
                            <div class="el-upload__text">
                                Drag & Drop or <em>Browse to Upload Contract</em>
                            </div>
                            <div class="el-upload__tip" slot="tip">
                                PDF, DOC, or DOCX files only (max 10MB)
                            </div>
                            </el-upload>
                        </div>
                  
                        <!-- Show newly uploaded file -->
                        <div v-else-if="contractForm.file" class="uploaded-file">
                            <div class="file-info">
                            <i class="el-icon-document text-primary"></i>
                            <span class="file-name">{{ contractForm.fileName }}</span>
                            <span class="file-status">(New file - will be processed on save)</span>
                            <base-button
                                type="danger"
                                size="sm"
                                icon
                                @click="removeFile"
                                class="remove-btn"
                            >
                                <i class="fa fa-times"></i>
                            </base-button>
                            </div>
                        </div>

                        <!-- Show existing file from API -->
                        <div v-else-if="contractForm.fileUrl" class="uploaded-file existing-file">
                            <!-- Warning message if content changed -->
                            <div v-if="contentHasChanged" class="content-changed-warning">
                                <i class="fa fa-exclamation-triangle text-warning"></i>
                                <span class="warning-text">
                                    Content has been modified. Please upload a new file to match the updated content.
                                </span>
                            </div>
                            
                            <div class="file-info">
                            <i class="el-icon-document text-primary"></i>
                            <span class="file-name">{{ contractForm.originalFilename }}</span>
                            <span class="file-details">
                                ({{ formatFileSize(contractForm.fileSize) }} {{ contractForm.fileType.toUpperCase() }})
                            </span>
                            <base-button
                                type="primary"
                                size="sm"
                                outline
                                @click="downloadFile"
                                class="download-btn"
                            >
                                <i class="fa fa-download"></i> Download
                            </base-button>
                            <base-button
                                type="secondary"
                                size="sm"
                                outline
                                @click="replaceFile"
                                class="replace-btn"
                                :class="{ 'btn-warning': contentHasChanged }"
                            >
                                <i class="fa fa-upload"></i> {{ contentHasChanged ? 'Replace Required' : 'Replace' }}
                            </base-button>
                            </div>
                        </div>
                    </div>
              </div>

              <div>
                <h6 class="heading-small text-muted mb-2">Contract Content *</h6>
                <html-editor
                  name="contract_content"
                  :value="contractForm.content"
                  @input="contractForm.content = $event"
                  placeholder="Enter or paste your contract content here, or upload a file above to auto-populate..."
                  class="contract-editor"
                  required
                ></html-editor>
              </div>
            </form>
          </card>
        </div>
      </div>

      <!-- Loading Overlay -->
      <div v-if="loading" class="loading-overlay">
        <div class="loading-content">
          <i class="el-icon-loading"></i>
          <p>Loading contract template...</p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { 
  Button, 
  Upload,
  Message
} from "element-ui";
import RouteBreadCrumb from "@/components/Breadcrumb/RouteBreadcrumb";
import HtmlEditor from "@/components/Inputs/HtmlEditor.vue";
import { SettingsService } from "@/services";

export default {
  name: "DefaultContract",
  // Note: This component uses direct service calls instead of Vuex for simplicity
  // since this is a low-frequency administrative feature
  components: {
    RouteBreadCrumb,
    [Button.name]: Button,
    [Upload.name]: Upload,
    [HtmlEditor.name]: HtmlEditor
  },
  data() {
    return {
      loading: false,
      saving: false,
      originalContent: '', // Store original content to detect changes
      userHasEditedContent: false, // Explicit flag for user edits
      isInitializing: true, // Flag to track if we're still loading initial data
      contractForm: {
        id: null,
        name: '',
        content: '',
        file: null,
        fileName: '',
        // File metadata from API
        fileUrl: '',
        originalFilename: '',
        fileType: '',
        fileSize: 0
      }
    };
  },
  computed: {
    processing() {
      return this.loading || this.saving;
    },
    contentHasChanged() {
      // Only show warning if user has explicitly edited content after load
      return this.userHasEditedContent && 
             this.originalContent !== '' && 
             this.contractForm.content !== this.originalContent;
    },
    needsNewFile() {
      // If content changed and still using old file, need to upload new file
      return this.contentHasChanged && this.contractForm.fileUrl && !this.contractForm.file;
    },
    canSave() {
      return this.contractForm.name.trim() && 
             this.contractForm.content.trim() && 
             (this.contractForm.file || this.contractForm.fileUrl) && 
             !this.needsNewFile && 
             !this.processing;
    }
  },
  watch: {
    'contractForm.content': {
      handler(newContent, oldContent) {
        // Only set the flag if this is a real user edit (not during initialization)
        if (!this.isInitializing && this.originalContent !== '' && oldContent !== undefined) {
          this.userHasEditedContent = true;
        }
      }
    }
  },
  mounted() {
    this.loadExistingContract();
  },
  methods: {
    /**
     * Extract error message from API response following the structured error format
     * @param {Object} error - The error object from the API response
     * @param {String} defaultMessage - Default message if no structured error is found
     * @returns {String} - The formatted error message
     */
    getErrorMessage(error, defaultMessage = 'An error occurred') {
      // Handle structured error response format
      if (error?.response?.data?.error) {
        const errorData = error.response.data.error;
        
        // Log error details for debugging
        console.warn(`API Error - ${errorData.type} (${errorData.code}): ${errorData.message}`);
        
        return errorData.message || defaultMessage;
      }
      
      // Fallback to older error format
      if (error?.response?.data?.message) {
        return error.response.data.message;
      }
      
      // Fallback to error message property
      if (error?.message) {
        return error.message;
      }
      
      return defaultMessage;
    },

    async loadExistingContract() {
      this.loading = true;
      try {
        const response = await SettingsService.getDefaultContract();
        
        if (response && response.data) {
          this.contractForm = {
            id: response.data.id,
            name: response.data.name || '',
            content: response.data.content || '',
            file: null,
            fileName: '',
            // Handle file metadata from API
            fileUrl: response.data.file_url || '',
            originalFilename: response.data.original_filename || '',
            fileType: response.data.file_type || '',
            fileSize: response.data.file_size || 0
          };
          
          // Store original content to detect changes
          this.originalContent = response.data.content || '';
          
          // If there's a file URL, set the fileName for display
          if (this.contractForm.fileUrl && this.contractForm.originalFilename) {
            this.contractForm.fileName = this.contractForm.originalFilename;
          }
        }
      } catch (error) {
        console.error('Error loading contract:', error);
        
        // Don't show error for empty state (404)
        if (error.response && error.response.status !== 404) {
          const errorMessage = this.getErrorMessage(error, 'Failed to load existing contract template');
          Message.error(errorMessage);
        }
      } finally {
        this.loading = false;
        // Mark initialization as complete after all data is loaded
        this.$nextTick(() => {
          this.isInitializing = false;
        });
      }
    },

    handleFileUpload(file) {
      // Validate file type
      const allowedTypes = [
        'application/pdf',
        'application/msword',
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
      ];
      
      if (!allowedTypes.includes(file.type)) {
        Message.error('Please select a valid PDF, DOC, or DOCX file');
        return false;
      }

      // Validate file size (max 10MB)
      const maxSize = 10 * 1024 * 1024; // 10MB
      if (file.size > maxSize) {
        Message.error('File size must be less than 10MB');
        return false;
      }

      // Store file for processing on save
      this.contractForm.file = file;
      this.contractForm.fileName = file.name;
      
      // Auto-generate name if not set
      if (!this.contractForm.name.trim()) {
        this.contractForm.name = file.name.replace(/\.[^/.]+$/, ""); // Remove extension
      }
      
      Message.success('File selected - will be processed when you save');
      return false; // Prevent auto upload
    },

    removeFile() {
      this.contractForm.file = null;
      this.contractForm.fileName = '';
      Message.success('File removed');
    },

    formatFileSize(bytes) {
      if (!bytes) return '0 B';
      const k = 1024;
      const sizes = ['B', 'KB', 'MB', 'GB'];
      const i = Math.floor(Math.log(bytes) / Math.log(k));
      return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    },

    downloadFile() {
      if (this.contractForm.fileUrl) {
        // Open file URL in new tab for download
        window.open(this.contractForm.fileUrl, '_blank');
      }
    },

    replaceFile() {
      // Clear existing file info to show upload area
      this.contractForm.fileUrl = '';
      this.contractForm.originalFilename = '';
      this.contractForm.fileType = '';
      this.contractForm.fileSize = 0;
      this.contractForm.fileName = '';
      
      // Reset the content change flag since we're replacing the file
      this.userHasEditedContent = false;
      
      Message.info('You can now upload a new file');
    },

    async saveContract() {
      // Enhanced validation for mandatory fields
      if (!this.contractForm.name.trim()) {
        Message.error('Contract name is required');
        return;
      }

      if (!this.contractForm.content.trim()) {
        Message.error('Contract content is required');
        return;
      }

      if (!this.contractForm.file && !this.contractForm.fileUrl) {
        Message.error('Contract file is required - please upload a file');
        return;
      }

      if (this.needsNewFile) {
        Message.error('Content has been modified. Please upload a new file to match the updated content.');
        return;
      }

      this.saving = true;
      
      try {
        const formData = new FormData();
        formData.append('name', this.contractForm.name);
        formData.append('content', this.contractForm.content);
        
        // Include file if selected for processing
        if (this.contractForm.file) {
          formData.append('file', this.contractForm.file);
          formData.append('fileName', this.contractForm.fileName);
        }
        
        if (this.contractForm.id) {
          formData.append('id', this.contractForm.id);
        }

        const response = await SettingsService.saveDefaultContract(formData);
        
        if (response && response.success !== false) {
          Message.success('Contract template saved successfully');
          
          // Update the ID if this was a new contract
          if (response.data && response.data.id) {
            this.contractForm.id = response.data.id;
          }
          
          // Clear file after successful save and reload data
          this.contractForm.file = null;
          this.contractForm.fileName = '';
          
          // Reset edit flag and reload fresh data from server
          this.userHasEditedContent = false;
          
          // Set initializing flag before reload to prevent watcher triggers
          this.isInitializing = true;
          await this.loadExistingContract();
        } else {
          throw new Error(response?.message || 'Save failed');
        }
      } catch (error) {
        console.error('Error saving contract:', error);
        
        const errorMessage = this.getErrorMessage(error, 'Failed to save contract template');
        Message.error(errorMessage);
      } finally {
        this.saving = false;
      }
    },

    resetForm() {
      this.isInitializing = true; // Mark as initializing during reset
      this.contractForm = {
        id: null,
        name: '',
        content: '',
        file: null,
        fileName: '',
        fileUrl: '',
        originalFilename: '',
        fileType: '',
        fileSize: 0
      };
      this.originalContent = '';
      this.userHasEditedContent = false; // Reset edit flag
      
      // Mark reset complete in next tick
      this.$nextTick(() => {
        this.isInitializing = false;
      });
      
      Message.success('Form reset');
    }
  }
};
</script>

<style scoped>
.contract-editor {
  min-height: 400px;
}

.text-primary {
  color: #5e72e4 !important;
}

.text-danger {
  color: #f56c6c;
}

.file-upload-section {
  margin-bottom: 2rem;
}

.contract-uploader {
  width: 100%;
}

.upload-area .el-upload-dragger {
  width: 100%;
  height: 120px;
  border: 2px dashed #d1d5db;
  border-radius: 8px;
  background-color: #f9fafb;
  transition: all 0.3s ease;
}

.upload-area .el-upload-dragger:hover {
  border-color: #5e72e4;
  background-color: #f8f9ff;
}

.upload-area .el-icon-upload {
  font-size: 32px;
  color: #8c939d;
  margin-bottom: 8px;
}

.upload-area .el-upload__text {
  color: #606266;
  font-size: 14px;
  line-height: 1.4;
}

.upload-area .el-upload__tip {
  color: #909399;
  font-size: 12px;
  margin-top: 8px;
}

.uploaded-file {
  padding: 12px 16px;
  background-color: #f8f9fa;
  border: 1px solid #e9ecef;
  border-radius: 6px;
  margin-bottom: 1rem;
}

.file-info {
  display: flex;
  align-items: center;
  gap: 12px;
}

.file-info .el-icon-document {
  font-size: 20px;
  color: #5e72e4;
}

.file-name {
  flex: 1;
  font-weight: 500;
  color: #525f7f;
  font-size: 14px;
}

.file-status {
  color: #28a745;
  font-size: 12px;
  font-style: italic;
  margin-right: 8px;
}

.file-details {
  color: #6c757d;
  font-size: 12px;
  margin-right: 8px;
}

.existing-file .file-info {
  flex-wrap: wrap;
  gap: 8px;
}

.download-btn,
.replace-btn {
  padding: 4px 8px !important;
  min-width: auto !important;
  border-radius: 4px !important;
  font-size: 12px !important;
}

.remove-btn {
  padding: 4px 8px !important;
  min-width: auto !important;
  border-radius: 4px !important;
}

.loading-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(255, 255, 255, 0.9);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.loading-content {
  text-align: center;
  color: #525f7f;
}

.loading-content i {
  font-size: 24px;
  margin-bottom: 8px;
  display: block;
}

.content-changed-warning {
  background-color: #fff3cd;
  border: 1px solid #ffc107;
  border-radius: 4px;
  padding: 8px 12px;
  margin-bottom: 12px;
  display: flex;
  align-items: center;
  gap: 8px;
}

.warning-text {
  color: #856404;
  font-size: 13px;
  font-weight: 500;
}

.text-warning {
  color: #ffc107 !important;
}

.btn-warning {
  background-color: #ffc107 !important;
  border-color: #ffc107 !important;
  color: #212529 !important;
}

.btn-warning:hover {
  background-color: #e0a800 !important;
  border-color: #d39e00 !important;
}
</style>